import { IWindowService } from 'angular';
import { AbstractGeoDataMap } from 'app/map/services/heatmap/AbstractGeoDataMap';
import { HeatMapOptions } from 'app/map/services/heatmap/heat_map_options_service';
import { HeatmapDataRequesterFactory } from 'app/map/services/heatmap/HeatmapDataRequesterFactory';
import { SelectableHeatMapFeature } from 'app/map/services/heatmap/types/SelectableHeatMapFeature';
import { ImsImageryLayersService } from 'app/map/services/ImsImageryLayersService';
import {
    DataWithLocationArray,
    GetWithLocationFilterArgs,
    ImsTimeCollection,
} from 'app/shared/collections/time/ims_time_collection.factory';
import { CachedEventRegistry } from 'app/shared/events/cached_event_registry';
import { ViewStateService } from 'app/shared/view_state.service';
import { IMSAggregator } from 'app/views/aggregators/aggregator';
import { IMS_TYPE__IP_DEVICE_STAT } from 'app/views/view_types/network/collections/ip_device_stat';
import { VectorHeatMapContext } from 'app/views/view_types/view_utils';
import { IMSVisualiser } from 'app/views/visualiser';
import moment from 'moment-timezone';
import { MapService } from '../map.service';
import { createGeoDataMap } from './GeoDataServiceHelpers';
import { ConfigService } from 'app/config/config.service';
import { cloneDeep } from 'lodash';
import ColorRenderer from 'app/shared/utilities/color_renderer';
import { randomColor } from 'app/lib/random_color';
import { ImsObjectUniqueColorRenderer } from 'app/shared/color/ImsObjectUniqueColorRenderer';

type CreateGeoDataMapFunc = (
    id: number,
    data: DataWithLocationArray,
    imsVisualiser: IMSVisualiser,
    imsAggregator: IMSAggregator,
    $window: IWindowService,
    imsImageryLayersService: ImsImageryLayersService,
    resolutionConfig: ResolutionConfig,
) => AbstractGeoDataMap;

export type ResolutionConfig = {
    choroplethSizeMetres: number;
    choroplethRenderSizeInPixels: number;
    resolutionMultiplier: number;
};

const HEAT_MAP_ABSOLUTE_MAX_LIMIT = 1_000_000_000;

interface GeoDataServiceOptions {
    // For test mocking
    createGeoDataMapFn?: CreateGeoDataMapFunc;
}

type SelectableFeaturesByGeoMapId = {
    [geoMapId: number]: SelectableHeatMapFeature[];
};

interface CreateGeoDataMapResult {
    // The ID of the added geo map
    geoMapId: number;
    // The number of rows returned
    dataCount: number;
    // True if dataCount has reached the fetch limit parameter - usually indicates
    // that the filter needs to be tightened
    fetchLimitReached: boolean;
    absoluteMaxPointsReached: boolean;
    heatmapChunksThatHaveFailed: Array<number>;
    reachedLimitOfTotalFailedChunks: boolean;
    heatmapData: DataWithLocationArray;
    updatedVisualiser: IMSVisualiser;
}

interface HeatMapDataRequesterRequest {
    dataByLocation: DataWithLocationArray;
    chunksThatHaveFailed: Array<number>;
    reachedLimitOfTotalFailedChunks: boolean;
}

/**
 * GeoDataService is responsible for the creation, removal and management of heatmaps within IMS.
 * At the moment it only handles a single heat map at a time.
 */
export class GeoDataService {
    private _mapService: MapService;
    private _viewStateService: ViewStateService;
    private _$window: IWindowService;
    private _imsImageryLayersService: ImsImageryLayersService;
    private _downloadProgressEventRegistry: CachedEventRegistry;
    private _idCount: number;
    private _heatmapDataRequesterFactory: HeatmapDataRequesterFactory;
    private _configService: ConfigService;

    private _store: { [key: number]: AbstractGeoDataMap };

    private readonly _createGeoDataMapFn: CreateGeoDataMapFunc;

    constructor(
        mapService: MapService,
        viewStateService: ViewStateService,
        $window: IWindowService,
        imsImageryLayersService: ImsImageryLayersService,
        options: GeoDataServiceOptions = {},
        heatmapDataRequesterFactory: HeatmapDataRequesterFactory,
        configService: ConfigService,
    ) {
        options = Object.assign(
            {
                // Defaults here
                createGeoDataMapFn: createGeoDataMap,
            },
            options,
        );

        this._createGeoDataMapFn = options.createGeoDataMapFn;
        this._heatmapDataRequesterFactory = heatmapDataRequesterFactory;

        this._configService = configService;
        this._mapService = mapService;
        this._viewStateService = viewStateService;
        this._$window = $window;
        this._imsImageryLayersService = imsImageryLayersService;
        this._downloadProgressEventRegistry = new CachedEventRegistry();
        this._store = {};
        this._idCount = 0;
    }

    public addOnGetHeatmapDataProgressUpdatedListener(
        listener: (heatMapLoadingBarPercentage: number) => any,
    ): () => void {
        return this._downloadProgressEventRegistry.addEventListener(listener);
    }

    lookupSelectableFeatures(
        id: number,
        latitude: number,
        longitude: number,
    ): SelectableFeaturesByGeoMapId {
        let geoMapIds = Object.keys(this._store);
        if (id) {
            // If we have an id passed in, limit the search to a single store
            geoMapIds = [id.toString()];
        }
        return geoMapIds.reduce((result, id) => {
            const geoData = this._store[id];
            if (!geoData) {
                // Early return, don't add anything
                return result;
            }
            const selectableFeatures = geoData.lookupSelectableFeatures(
                latitude,
                longitude,
                Infinity,
            );
            if (selectableFeatures.length > 0) {
                result[id] = selectableFeatures;
            }
            return result;
        }, {});
    }

    get(geoDataID: number): AbstractGeoDataMap {
        return this._store[geoDataID];
    }

    add(
        data: DataWithLocationArray,
        imsVisualiser: IMSVisualiser,
        imsAggregator: IMSAggregator,
        resolutionConfig: ResolutionConfig,
    ): number {
        // For now, only 1 layer at a time is allowed
        this.deleteLastLayer();

        const id = this._generateID();

        const geoDataMap = this._createGeoDataMapFn(
            id,
            data,
            imsVisualiser,
            imsAggregator,
            this._$window,
            this._imsImageryLayersService,
            resolutionConfig,
        );
        geoDataMap.drawOnViewer(this._mapService.viewer);
        this._store[id] = geoDataMap;

        return id;
    }

    remove(id: number): void {
        const geoData = this._store[id];
        if (!geoData) {
            throw 'No ID Found';
        }
        geoData.delete();
        this._store[id] = undefined;
    }

    // Dumb way of generating unique IDs
    private _generateID(): number {
        ++this._idCount;
        return this._idCount;
    }

    private async _makeHeatMapRequests(
        columnName: string,
        filterArgs: GetWithLocationFilterArgs,
        timeCollection: ImsTimeCollection,
        hoursRequested: number,
        maxPoints: number,
        vectorHeatMapContext: VectorHeatMapContext | null,
    ): Promise<HeatMapDataRequesterRequest> {
        const heatMapDataRequester = this._heatmapDataRequesterFactory.create(
            columnName,
            filterArgs,
            timeCollection,
            hoursRequested,
            maxPoints,
            this._downloadProgressEventRegistry,
            vectorHeatMapContext,
            this._configService,
        );

        const heatmapData = await heatMapDataRequester.makeHeatMapRequests();
        const chunksThatHaveFailed = heatMapDataRequester.chunksThatHaveFailed;
        const reqFailedChunkFailures = heatMapDataRequester.reachedLimitOfTotalFailedChunks;

        this._downloadProgressEventRegistry.clearCache();

        return {
            dataByLocation: heatmapData,
            chunksThatHaveFailed: chunksThatHaveFailed,
            reachedLimitOfTotalFailedChunks: reqFailedChunkFailures,
        };
    }

    /**
     *
     * @param imsVisualiser
     * @param imsAggregator
     * @param filterOpts
     * @param resolutionConfig
     * @returns {number} the ID of the newly created geo data map
     */
    async createGeoDataMap(
        imsVisualiser: IMSVisualiser,
        imsAggregator: IMSAggregator,
        filterOpts: HeatMapOptions,
        resolutionConfig: ResolutionConfig,
    ): Promise<CreateGeoDataMapResult> {
        // Column to request from API
        const columnName = imsVisualiser.valueGetter.statKey;
        const timeCollection: ImsTimeCollection = imsVisualiser.valueGetter.statCollection; //whatever the relevant collection is

        if (!filterOpts.endTimestamp) {
            throw new Error(`filterOpts.endTimestamp must be truthy`);
        }

        if (!filterOpts.startTimestamp) {
            throw new Error(`filterOpts.startTimestamp must be truthy`);
        }

        const startTime = filterOpts.startTimestamp.toISOString();
        const endTime = filterOpts.endTimestamp.toISOString();

        const currentView = this._viewStateService.getCurrentView();

        const filterArgs: GetWithLocationFilterArgs = {
            startTime: startTime,
            endTime: endTime,
            ipDeviceTypeIds: filterOpts.filterByIpDeviceTypeId
                ? [filterOpts.filterByIpDeviceTypeId]
                : null,
            ipDeviceTagIds: filterOpts.filterByIpDeviceTagIds,
            notAssetIds: setToNumberArray(filterOpts.filterByNotAssetIds),
            assetIds: setToNumberArray(filterOpts.filterByAssetIds),
            siteIds: filterOpts.siteId ? [filterOpts.siteId] : null,
            ignoredStatFilterValue: filterOpts.includeIgnored ? null : false,
            interpolationLimitSeconds: filterOpts.interpolationLimitSeconds,
            assetTypeMobilityList: filterOpts.assetTypeMobilityList,
            statFilters: filterOpts.statFilters,
            viewId: currentView ? currentView.id : null,
        };

        const duration = moment.duration(filterOpts.endTimestamp.diff(filterOpts.startTimestamp));
        const hoursRequested = duration.asHours();
        const maxPoints = filterOpts.limit || HEAT_MAP_ABSOLUTE_MAX_LIMIT;

        const result = await this._makeHeatMapRequests(
            columnName,
            filterArgs,
            timeCollection,
            hoursRequested,
            maxPoints,
            imsVisualiser.vectorHeatMapContext,
        );

        let dataByLocation = result.dataByLocation;
        const chunksThatHaveFailed = result.chunksThatHaveFailed;
        const reachedLimitOfTotalFailedChunks = result.reachedLimitOfTotalFailedChunks;

        // We don't want to use the ims ip device stat filter funcs because the only
        // thing that is included in it is the ignored stat filter. The heat map
        // filter options already has this included so we will rely on the
        // API to filter out the ignored stats.
        if (timeCollection.getImsType() !== IMS_TYPE__IP_DEVICE_STAT) {
            // This leaves filter functions provided by stat_display_filter components
            // to be run in this if-statement.

            // Filter out unwanted stats from the heat map using the standard vis function
            const statFilterFn = imsVisualiser.valueGetter.statFilterFnGenerator();
            if (statFilterFn) {
                dataByLocation = dataByLocation.filter(statFilterFn);
            }
        }

        // Initialising duplicateVisualiser
        let duplicateVisualiser: IMSVisualiser;

        // As we are updating the colorRenderer in IMSVisualiser,
        // we don't use duplicate visualiser for _ims_network_stat_connected_ap
        // Hack to know what devices are in use for the heatmap
        if (imsVisualiser.logicalName === '_ims_network_stat_connected_ap') {
            const heatMapNextHopDeviceIds = [
                ...new Set(result.dataByLocation.map((point) => point['next_hop_ip_device_id'])),
            ];
            const allIpDeviceNamesAndColors = imsVisualiser.colorRenderer['_collection'].all;
            const colorPoints = heatMapNextHopDeviceIds.map((item) => ({
                value: allIpDeviceNamesAndColors[item]._shortName,
                color: allIpDeviceNamesAndColors[item]._uniqueCesiumColor,
            }));

            (imsVisualiser.colorRenderer as ImsObjectUniqueColorRenderer).setPoints(colorPoints);
        }

        // We use duplicate visualiser for lte_stats_eci as we are updating the colorRenderer
        // only for heatmaps but do not want the color Renderer to be updated for the other (dashboards and Map Options)
        // Hack to know what ECI's are in use for the heatmap
        if (imsVisualiser.logicalName === 'lte_stats_eci') {
            // We clone the IMSVisualiser to duplicateVisualiser and make changes to that
            // IMSVisualiser stays untouched
            duplicateVisualiser = cloneDeep(imsVisualiser);

            duplicateVisualiser.colorRenderer = new ColorRenderer({
                points: [],
                type: 'exact',
                default: [255, 255, 255, 1], // white
            });

            // We are assigning colors to the unique ECI's that are in use for the heatmap
            const uniqueEcis = [...new Set(result.dataByLocation.map((point) => point['eci']))];
            const eciWithColors = assignCesiumColors(uniqueEcis);

            (duplicateVisualiser.colorRenderer as ColorRenderer).points = Object.keys(
                eciWithColors,
            ).map((key) => ({
                value: key,
                color: eciWithColors[key],
            }));
        }

        const geoMapId = this.add(
            dataByLocation,
            duplicateVisualiser || imsVisualiser,
            imsAggregator,
            resolutionConfig,
        );
        return {
            geoMapId,
            dataCount: dataByLocation.length,
            fetchLimitReached: dataByLocation.length >= maxPoints,
            absoluteMaxPointsReached: maxPoints === HEAT_MAP_ABSOLUTE_MAX_LIMIT,
            heatmapChunksThatHaveFailed: chunksThatHaveFailed,
            reachedLimitOfTotalFailedChunks: reachedLimitOfTotalFailedChunks,
            heatmapData: dataByLocation,
            updatedVisualiser: duplicateVisualiser, // we pass it back so that we can update the colorRenderer behaviour for lte_stats_eci
        };
    }

    deleteLastLayer(): void {
        // Deletes all maps
        Object.values(this._store).forEach((map) => map.delete());
        this._store = {};
    }
}

function setToNumberArray(s: Set<number>): number[] {
    if (s && s.size > 0) {
        const result = [];
        s.forEach((value) => result.push(value));
        return result;
    }
    return null;
}

function assignCesiumColors(items: string[]): { [key: string]: Cesium.Color } {
    const colorDict: { [key: string]: Cesium.Color } = {};
    const usedColors = new Set<string>();

    for (const item of items) {
        let uniqueColorString = randomColor();

        // Keep generating a new color until it is unique
        while (usedColors.has(uniqueColorString)) {
            uniqueColorString = randomColor();
        }

        // Assign unique Cesium color string to the item in the dictionary
        colorDict[item] = Cesium.Color.fromCssColorString(uniqueColorString);

        // Add the color to the usedColors set to avoid duplicates
        usedColors.add(uniqueColorString);
    }

    return colorDict;
}
